import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Settings,
  ChevronLeft,
  ChevronRight,
  HelpCircle, 
  FileText,
  Info,
  ExternalLink,
  LogIn,
  LogOut,
  Mail,
  User,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion'; 
import { Button } from '../../components/ui/button';
import { logoutUser } from '../../slices/authSlice';
import Logo from '../../components/Logo/Logo';

// Components
import AccountSettingsModal, { SETTINGS_TYPES } from '../../components/AccountSettingsModal/AccountSettingsModal';
import ProfileInfo from './ProfileInfo/ProfileInfo';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import ChangeLocation from '../../components/ChangeLocation/ChangeLocation';
import FAQ from '../../components/FAQ/FAQ';
import TermsOfService from '../../components/TermsOfService/TermsOfService';
import SocialMediaLinks from '../../components/SocialMediaLinks/SocialMediaLinks';
import BlockedUsersList from '../../components/BlockedUsersList';
import NotificationPreferences from '../../components/NotificationPreferences';

const Profile = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(state => state.auth.isAuthenticated);
  const user = useSelector(state => state.auth.user);
  const navigate = useNavigate();
  
  // Accordion state - default to 'account' being open
  const [expandedSection, setExpandedSection] = useState('account');
  
  // Modal states
  const [showMyInfo, setShowMyInfo] = useState(false);
  const [showLanguage, setShowLanguage] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showBlockedUsers, setShowBlockedUsers] = useState(false);
  const [showNotificationPrefs, setShowNotificationPrefs] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 border-b border-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
              {/* Back button */}
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate(-1)} 
                className="min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px]"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-foreground" />
              </Button>
              <div className="min-w-0 flex-1">
                <motion.h1
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="text-lg sm:text-xl md:text-2xl font-bold text-foreground"
                >
                  {t('menu', 'Menu')}
                </motion.h1>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1 line-clamp-1">
                  {t('manage_account', 'Manage your account and preferences')}
                </p>
              </div>
            </div>
            <Logo className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex-shrink-0" />
          </div>
        </div>
      </div>

      {/* Desktop: Two-column layout, Mobile: Single column */}
      <div className="max-w-6xl mx-auto md:pt-8 md:px-6 lg:px-8">
        <div className="md:grid md:grid-cols-12 md:gap-8">
          
          {/* Sidebar / Main Content on Mobile */}
          <div className="md:col-span-4 lg:col-span-3">
            {/* Desktop*/}
            <div className="md:sticky md:top-24">

              {/* App Logo & Profile Card */}
              <div className="bg-white md:rounded-2xl md:shadow-sm md:border md:border-border overflow-hidden">
                {/* App Logo Section */}
                <div className="px-4 py-4 sm:px-6 sm:py-6 flex flex-col items-center border-b border-border bg-gradient-to-b from-primary/5 to-transparent">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 mb-2 sm:mb-3">
                    <Logo className="w-full h-full" showTrademark={false} />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-primary">All Animal App</h3>
                  <p className="text-[10px] sm:text-xs text-muted-foreground text-center mt-1">
                    {t('app_tagline', 'Connecting animals & communities worldwide')}
                  </p>
                </div>

                {/* Profile Section - Only when logged in */}
                {isLoggedIn && user && (
                  <div className="px-4 md:px-5 py-5 border-b border-border bg-primary/5">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base font-semibold text-foreground truncate">
                          {user.firstName || user.username || t('user', 'User')}
                        </h3>
                        <p className="text-xs text-muted-foreground break-all line-clamp-2">
                          {user.email}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sign In Button - Only when NOT logged in */}
                {!isLoggedIn && (
                  <div className="px-4 py-5 sm:px-6 sm:py-6 border-b border-border">
                    <button
                      onClick={handleLoginClick}
                      className="w-full min-h-[44px] sm:min-h-[48px] bg-primary hover:bg-primary/90 text-white text-sm sm:text-base font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors active:scale-[0.98]"
                    >
                      <LogIn className="w-4 h-4 sm:w-5 sm:h-5" />
                      {t('sign_in', 'Sign In')}
                    </button>
                    <p className="text-[10px] sm:text-xs text-center text-muted-foreground mt-2 sm:mt-3">
                      {t('sign_in_benefits', 'Sign in to create posts, save favorites, and message other users')}
                    </p>
                    <p className="text-[10px] sm:text-xs text-center text-muted-foreground mt-1.5 sm:mt-2">
                      {t('no_account', "Don't have an account?")}{' '}
                      <Link
                        to="/sign-up"
                        className="text-primary hover:text-primary/80 font-semibold underline transition-colors"
                      >
                        {t('sign_up_here', 'Sign up here')}
                      </Link>
                    </p>
                  </div>
                )}

                {/* Social Media - Desktop Sidebar */}
                <div className="hidden md:block py-4 px-4 sm:py-6 sm:px-6">
                  <p className="text-xs text-center text-muted-foreground mb-3 sm:mb-4">
                    {t('stay_connected', 'Connect with us')}
                  </p>
                  <SocialMediaLinks variant='profile' />
                </div>
              </div>
            </div>
          </div>

          {/* Main Menu Content */}
          <div className="md:col-span-8 lg:col-span-9">
            {/* Menu Card */}
            <div className="bg-white md:rounded-2xl md:shadow-sm md:border md:border-border overflow-hidden">
              {/* Account Settings - Accordion */}
              <div>
                <button
                  onClick={() => toggleSection('account')}
                  className="w-full px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between hover:bg-secondary/30 transition-colors min-h-[52px] sm:min-h-[56px] active:bg-secondary/50"
                >
                  <div className="flex items-center gap-2.5 sm:gap-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    </div>
                    <span className="font-medium text-sm sm:text-base text-foreground">{t('account_settings', 'Account Settings')}</span>
                  </div>
                  <ChevronRight
                    className={`w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground transition-transform duration-200 ${
                      expandedSection === 'account' ? 'rotate-90' : ''
                    }`}
                  />
                </button>
                
                <AnimatePresence>
                  {expandedSection === 'account' && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden bg-secondary/20"
                    >
                      <div className="py-2">
                        {isLoggedIn && (
                          <button
                            onClick={() => setShowMyInfo(true)}
                            className="w-full px-4 sm:px-6 md:px-12 py-3 text-left text-sm text-foreground hover:bg-secondary/30 active:bg-secondary/50 transition-colors min-h-[44px] flex items-center"
                          >
                            <span className="ml-4 sm:ml-6 md:ml-0">{t('my_info', 'My Info')}</span>
                          </button>
                        )}
                        
                        {isLoggedIn && (
                          <Link
                            to="/reset-password"
                            className="block w-full px-4 sm:px-6 md:px-12 py-3 text-left text-sm text-foreground hover:bg-secondary/30 active:bg-secondary/50 transition-colors min-h-[44px] flex items-center"
                          >
                            <span className="ml-4 sm:ml-6 md:ml-0">{t('change_password', 'Change PW')}</span>
                          </Link>
                        )}
                        
                        <button
                          onClick={() => setShowLanguage(true)}
                          className="w-full px-4 sm:px-6 md:px-12 py-3 text-left text-sm text-foreground hover:bg-secondary/30 active:bg-secondary/50 transition-colors min-h-[44px] flex items-center"
                        >
                          <span className="ml-4 sm:ml-6 md:ml-0">{t('language', 'Language')}</span>
                        </button>
                        
                        {isLoggedIn && (
                          <button
                            onClick={() => setShowLocation(true)}
                            className="w-full px-4 sm:px-6 md:px-12 py-3 text-left text-sm text-foreground hover:bg-secondary/30 active:bg-secondary/50 transition-colors min-h-[44px] flex items-center"
                          >
                            <span className="ml-4 sm:ml-6 md:ml-0">{t('location', 'Location')}</span>
                          </button>
                        )}

                        {isLoggedIn && (
                          <button
                            onClick={() => setShowBlockedUsers(true)}
                            className="w-full px-4 sm:px-6 md:px-12 py-3 text-left text-sm text-foreground hover:bg-secondary/30 active:bg-secondary/50 transition-colors min-h-[44px] flex items-center"
                          >
                            <span className="ml-4 sm:ml-6 md:ml-0">{t('blocked_users', 'Blocked Users')}</span>
                          </button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {isLoggedIn && (
                <>
                  <div className="border-t border-border" />
                  <button
                    onClick={() => setShowNotificationPrefs(true)}
                    className="w-full px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between hover:bg-secondary/30 active:bg-secondary/50 transition-colors min-h-[52px] sm:min-h-[56px]"
                  >
                    <div className="flex items-center gap-2.5 sm:gap-3">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                      </div>
                      <span className="font-medium text-sm sm:text-base text-foreground">
                        {t('email_notifications_section_title', 'Email Notifications')}
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                  </button>
                </>
              )}

              {/* Divider */}
              <div className="border-t border-border" />

              {/* FAQ */}
              <button
                onClick={() => setShowFAQ(true)}
                className="w-full px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between hover:bg-secondary/30 active:bg-secondary/50 transition-colors min-h-[52px] sm:min-h-[56px]"
              >
                <div className="flex items-center gap-2.5 sm:gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-foreground">{t('faq', 'FAQ')}</span>
                </div>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              </button>

              {/* Terms of Service */}
              <button
                onClick={() => setIsTermsOpen(true)}
                className="w-full px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between hover:bg-secondary/30 active:bg-secondary/50 transition-colors min-h-[52px] sm:min-h-[56px]"
              >
                <div className="flex items-center gap-2.5 sm:gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-foreground">{t('terms_of_service', 'Terms of Service')}</span>
                </div>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              </button>

              {/* About Us - External Link */}
              <a
                href="https://about.allanimalapp.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between hover:bg-secondary/30 active:bg-secondary/50 transition-colors min-h-[52px] sm:min-h-[56px]"
              >
                <div className="flex items-center gap-2.5 sm:gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Info className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-foreground">{t('about_us', 'About Us')}</span>
                </div>
                <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              </a>

              {/* Divider before Logout */}
              {isLoggedIn && <div className="border-t border-border" />}

              {/* Logout - Only when logged in */}
              {isLoggedIn && (
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between hover:bg-red-50 active:bg-red-100 transition-colors min-h-[52px] sm:min-h-[56px]"
                >
                  <div className="flex items-center gap-2.5 sm:gap-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <LogOut className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                    </div>
                    <span className="font-medium text-sm sm:text-base text-red-600">{t('logout', 'Logout')}</span>
                  </div>
                </button>
              )}
            </div>

            {/* Social Media  */}
            <div className="md:hidden border-t border-border py-4 px-4 sm:py-6 sm:px-6 bg-white mt-4">
              <p className="text-[10px] sm:text-xs text-center text-muted-foreground mb-3 sm:mb-4">
                {t('stay_connected', 'Connect with us')}
              </p>
              <SocialMediaLinks variant='profile' />
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AccountSettingsModal
        isOpen={showMyInfo}
        onClose={() => setShowMyInfo(false)}
        type={SETTINGS_TYPES.MY_INFO}
      >
        <ProfileInfo />
      </AccountSettingsModal>

      <AccountSettingsModal
        isOpen={showLanguage}
        onClose={() => setShowLanguage(false)}
        type={SETTINGS_TYPES.LANGUAGE}
      >
        <LanguageSwitcher />
      </AccountSettingsModal>

      <AccountSettingsModal
        isOpen={showLocation}
        onClose={() => setShowLocation(false)}
        type={SETTINGS_TYPES.LOCATION}
      >
        <ChangeLocation />
      </AccountSettingsModal>

      <AccountSettingsModal
        isOpen={showFAQ}
        onClose={() => setShowFAQ(false)}
        type={SETTINGS_TYPES.FAQ}
      >
        <FAQ />
      </AccountSettingsModal>

      <AccountSettingsModal
        isOpen={showBlockedUsers}
        onClose={() => setShowBlockedUsers(false)}
        type={SETTINGS_TYPES.BLOCKED_USERS}
      >
        <BlockedUsersList />
      </AccountSettingsModal>

      <AccountSettingsModal
        isOpen={showNotificationPrefs}
        onClose={() => setShowNotificationPrefs(false)}
        type={SETTINGS_TYPES.NOTIFICATION_PREFERENCES}
      >
        <NotificationPreferences />
      </AccountSettingsModal>

      <TermsOfService isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
    </div>
  );
};

export default Profile;
